import { useState } from "react";
import { toast } from "sonner";
import { X, Loader2, CheckCircle2 } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import MFld from "./MFld";
import { formatEU } from "./formatters";

/**
 * InquiryModal — Upon-Request checkout replacement (v3.8.6).
 *
 * For Car Groups marked `upon_request`. Collects the guest's identity +
 * a free-form notes field, posts to `/api/public/inquiry` — the backend
 * creates a lightweight booking with status `inquiry_request` (no
 * pricing, no conflict check, no payment). Ops follows up manually.
 */
export default function InquiryModal({ vehicle, form, onClose }) {
  const [state, setState] = useState({
    full_name: "",
    email:     "",
    phone:     "",
    notes:     "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!state.full_name.trim() || !state.email.trim() || !state.phone.trim()) {
      toast.warning("Name, email and phone are required.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/public/inquiry", {
        vehicle_id:      vehicle.id,
        full_name:       state.full_name.trim(),
        email:           state.email.trim(),
        phone:           state.phone.trim(),
        pickup_date:     form.pickup_date,
        return_date:     form.return_date,
        pickup_time:     form.pickup_time,
        return_time:     form.return_time,
        pickup_location: form.pickup_loc,
        return_location: form.return_loc,
        notes:           state.notes.trim(),
      });
      setDone(data);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="inquiry-modal">
      <div className="bg-white text-neutral-900 max-w-lg w-full rounded-lg p-8 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        {done ? (
          <div className="text-center py-6" data-testid="inquiry-done">
            <CheckCircle2 size={48} className="text-emerald-500 mx-auto mb-4" />
            <h3 className="font-display text-2xl mb-2">Inquiry received</h3>
            <p className="text-sm text-neutral-500 leading-relaxed">
              {done.message}
            </p>
            <button
              type="button"
              onClick={onClose}
              className="mt-6 bg-black text-white text-sm px-6 py-2.5 rounded hover:bg-neutral-800"
              data-testid="inquiry-close-btn"
            >
              Close
            </button>
          </div>
        ) : (
          <>
            <div className="flex justify-between items-start mb-4">
              <div>
                <p className="text-[10px] tracking-[0.25em] uppercase text-amber-700">Upon Request</p>
                <h3 className="font-display text-2xl mt-1">{vehicle.name}</h3>
                <p className="text-xs text-neutral-500 mt-1">
                  {formatEU(form.pickup_date)} {form.pickup_time} → {formatEU(form.return_date)} {form.return_time}
                </p>
              </div>
              <button onClick={onClose} aria-label="Close" data-testid="inquiry-close-x"><X /></button>
            </div>
            <p className="text-xs text-neutral-500 mb-4 leading-relaxed">
              Instant booking is not available for this vehicle. Send us your details and we will come back to you with a tailored quote as soon as possible.
            </p>
            <form onSubmit={submit} className="space-y-4">
              <MFld label="Full name">
                <input required value={state.full_name} onChange={(e) => setState({ ...state, full_name: e.target.value })} className="pb-input" data-testid="inquiry-name" />
              </MFld>
              <div className="grid grid-cols-2 gap-4">
                <MFld label="Email">
                  <input required type="email" value={state.email} onChange={(e) => setState({ ...state, email: e.target.value })} className="pb-input" data-testid="inquiry-email" />
                </MFld>
                <MFld label="Phone">
                  <input required value={state.phone} onChange={(e) => setState({ ...state, phone: e.target.value })} className="pb-input" data-testid="inquiry-phone" />
                </MFld>
              </div>
              <MFld label="Notes (optional)">
                <textarea rows={3} value={state.notes} onChange={(e) => setState({ ...state, notes: e.target.value })} className="pb-input" data-testid="inquiry-notes" />
              </MFld>
              <button type="submit" disabled={busy} className="w-full bg-amber-500 text-white text-sm font-medium py-3 rounded hover:bg-amber-600 disabled:opacity-50 inline-flex items-center justify-center gap-2" data-testid="inquiry-submit">
                {busy ? <Loader2 size={14} className="animate-spin" /> : null}
                {busy ? "Sending…" : "Send inquiry"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
